import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/shadcn/sheet';
import { useTranslations } from '@/hooks/useTranslations';
import type { CalculationRequestOptions } from '../types';
import { CalculationRequestForm } from './CalculationRequestForm';

interface CreateCalculationRequestSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** The lead the calculation is raised against — fixed, so the form locks its picker. */
    leadId: string;
    options: Pick<CalculationRequestOptions, 'phases' | 'leads'>;
}

/**
 * The create form in a right-hand sheet, for the entry points that already sit on
 * the record the calculation belongs to — raising one should not take the lead off
 * the screen. Radix unmounts the content on close, so every open starts a fresh
 * form; submitting still follows the redirect to the new request.
 */
export function CreateCalculationRequestSheet({ open, onOpenChange, leadId, options }: CreateCalculationRequestSheetProps) {
    const { t } = useTranslations();

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="right" className="w-full gap-0 p-0 sm:w-[46rem] sm:max-w-[95vw]">
                <SheetHeader className="border-b px-6 py-4">
                    <SheetTitle>{t('calculation_requests.create.heading', 'New calculation request')}</SheetTitle>
                    <SheetDescription>{t('calculation_requests.create.subheading', 'Ask engineering to price a project')}</SheetDescription>
                </SheetHeader>
                {/* The form's own sticky action bar hangs off this scroll container. */}
                <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-4">
                    <CalculationRequestForm options={options} presetLeadId={leadId} onCancel={() => onOpenChange(false)} />
                </div>
            </SheetContent>
        </Sheet>
    );
}
